import { Period } from './core.models';

export interface TextPositioning {
  h_align: string;
  v_align: string;
  font_size: number;
  color: string;
  padding_top: number;
  padding_bottom: number;
  padding_left: number;
  padding_right: number;
  margin_bottom: number;
}

export interface Legend {
  layout: string;
  h_align: string;
  v_align: string;
  item_width: number;
  symbol_size: number;
  font_size: number;
  labels_color: string;
}

export interface ChartSettings {
  spacing_top: number;
  spacing_bottom: number;
  spacing_left: number;
  spacing_right: number;
  margin_top: string;
  margin_bottom: string;
  margin_left: string;
  margin_right: string;
}

export interface LabelSettings {
  format: string;
  font_size: number;
  inside: string;
  distance: number;
  x_offset: string;
  y_offset: string;
  rotation: string;
  color: string;
  align: string;
  padding_left: string;
  padding_right: string;
}

export interface Axis {
  title: AxisTitle;
  ticks: AxisTicks;
  labels: AxisLabels;
  max_padding: number;
  line_color: string;
  line_width: number;
  grid_line_color: string;
  grid_line_width: number;
}

export interface AxisTitle {
  font_size: number;
  rotation: string;
  color: string;
  align: string;
}

export interface AxisTicks {
  tick_color: string;
  tick_length: number;
  tick_width: number;
  tick_mark_placement: string;
}

export interface AxisLabels {
  font_size: number;
  wrap: string;
  rotation: string;
  color: string;
}

export interface Chart {
  id: number;
  name: string;
  description: string;
  default_settings: ChartDefaultSettings;
  is_auto_configured: number;
}

export interface ChartDefaultSettings {
  chart_stacking: string;
}

export interface WidgetVizType {
  id: number;
  name: string;
  widget_type_id: number;
  description: string;
  count_datasource_minimum: number;
  count_datasource_limit: number;
  icon_class: string;
  key: string;
  sizeX: number;
  sizeY: number;
  minimumSizeX: number;
  minimumSizeY: number;
  open_settings: boolean;
  is_pivotable: boolean;
  chart_type_name: string;
}

export interface WidgetPreset {
  id: number;
  name: string;
  group_id: number;
  description: string;
  is_auto_configured: number;
  display_order: number;
  key: string;
  chart_types: WidgetVizType[];
}

export interface CreateDashboardPageParams {
  dashboardId: string | number;
  name?: string;
  description?: string;
  durationSeconds?: number;
  titleFontSize?: number;
  period?: Period;
}

export interface UpdateDashboardPageParams {
  dashboard_id: string | number;
  page_id: number;
  name?: string;
  description?: string;
  duration_seconds?: number;
  period?: Period;
  is_visible_in_presentation?: boolean;
}

export interface ReorderWidgetPageParams {
  dashboardId: string | number;
  orderedPageIds: number[];
}

export enum VisualizationType {
  AggregatedFunctions = 'AggregatedFunctions',
  AreaSpline = 'AreaSpline',
  Area = 'Area',
  Bar = 'Bar',
  Cloud = 'Cloud',
  Column = 'Column',
  GeoMap = 'GeoMap',
  Heatmap = 'Heatmap',
  Line = 'Line',
  SentimentAverage = 'SentimentAverage',
  Pie = 'Pie',
  Spline = 'Spline',
  Summary = 'Summary',
  Table = 'Table',
  Html = 'Html',
  PostList = 'PostList',
  Treemap = 'Treemap',
  Section = 'Section',

  UserDefinedVariables = 'UserDefinedVariables',
}

export const chartTypeIdToVisualizationTypeMap = new Map<
  number,
  VisualizationType
>([
  [1, VisualizationType.Pie],
  [2, VisualizationType.Bar],
  [3, VisualizationType.Area],
  [4, VisualizationType.Column],
  [5, VisualizationType.GeoMap],
  [6, VisualizationType.Table],
  [7, VisualizationType.SentimentAverage],
  [8, VisualizationType.Treemap],
  [9, VisualizationType.Spline],
  [10, VisualizationType.Line],
  [11, VisualizationType.Heatmap],
  [12, VisualizationType.Pie],
  [13, VisualizationType.Bar],
  [14, VisualizationType.AreaSpline],
  [15, VisualizationType.Column],
  [16, VisualizationType.GeoMap],
  [17, VisualizationType.Table],
  [18, VisualizationType.SentimentAverage],
  [19, VisualizationType.Treemap],
  [20, VisualizationType.Spline],
  [21, VisualizationType.Line],
  [22, VisualizationType.Heatmap],
  [23, VisualizationType.Bar], // volume bar chart comparable
  [24, VisualizationType.Column], // column volume
  [25, VisualizationType.Summary],
  [26, VisualizationType.Bar], // bar combined
  [27, VisualizationType.Column], // column combined
  [28, VisualizationType.PostList], // Assuming 'Top Online Content' maps to 'UserDefinedVariables'
  [29, VisualizationType.PostList], // Assuming 'Top Facebook Content' maps to 'UserDefinedVariables'
  [30, VisualizationType.PostList], // Assuming 'Top Twitter Content' maps to 'UserDefinedVariables'
  [34, VisualizationType.PostList], //
  [36, VisualizationType.Bar], //
  [37, VisualizationType.Section], // Assuming 'Section Heading' maps to 'Section'
  [40, VisualizationType.Cloud],
  [42, VisualizationType.UserDefinedVariables], // Assuming 'Top Source Content' maps to 'UserDefinedVariables'
  [43, VisualizationType.AreaSpline],
  [44, VisualizationType.AreaSpline],
  [45, VisualizationType.Html],
]);

export enum NomChartType {
  PieChart = 1,
  BarChart = 2,
  VolumeChart = 3,
  ColumnChartPivot = 4,
  GeographicMap = 5,
  Table = 6,
  SentimentChart = 7,
  Treemap = 8,
  LineChart = 9,
  SentimentLine = 10,
  Heatmap = 11,
  PieChartComparative = 12,
  BarChartComparative = 13,
  VolumeChartComparative = 14,
  ColumnChartPivotComparative = 15,
  GeographicMapComparative = 16,
  TableComparative = 17,
  SentimentChartComparative = 18,
  TreemapComparative = 19,
  LineChartComparative = 20,
  SentimentLineComparative = 21,
  HeatmapComparative = 22,
  VolumeBarChartComparable = 23,
  ColumnVolume = 24,
  Summary = 25,
  BarCombined = 26,
  ColumnCombined = 27,
  TopOnlineContent = 28,
  TopFacebookContent = 29,
  TopTwitterContent = 30,
  Donut = 31,
  // Note: 32 is missing
  VolumePieChartComparable = 33,
  FeedContent = 34,
  // Note: 35 is missing
  SentimentPerCountry = 36,
  SectionTitle = 37,
  ExportVariable = 38,
  ExportVariableSystem = 39,
  TagCloud = 40,
  ExternalMetric = 41,
  Html = 45,
}

export const duplicateChartTypesWithVisualization = (
  chartTypes: WidgetVizType[],
): WidgetVizType[] => {
  const uniqueVisualizationTypes = new Set<VisualizationType>();

  return chartTypes.filter((chartType) => {
    const vizTypeId = chartTypeIdToVisualizationTypeMap.get(chartType.id);
    if (uniqueVisualizationTypes.has(vizTypeId)) {
      return false;
    }
    uniqueVisualizationTypes.add(vizTypeId);
    return true;
  });
};

export interface WidgetVizTypeChange {
  widgetTypeId: number;
  charTypeId: number;
}
